import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { Game } from '../../features/game/types/Game'
import { getConceptsReadBack } from '../features/analysis-screenshots/helpers/screenshotHelpers'
import { ConceptMatchGroup, ConceptsReadBack } from '../features/analysis-screenshots/types/AnalysisScreenshots'
import {
  bulkAssignGameFields,
  bulkAssignMarketIso,
  choiceByChoiceFeatureLegacyIds,
  filterByAnyConcept,
  getConceptListReadBack,
  mergeConceptsReadBacks,
} from '../features/implementation-bulk-assign/helpers/bulkAssignHelpers'
import { BulkAssignCounts, BulkAssignSelection, BulkAssignStoredChoice } from '../features/implementation-bulk-assign/types/ImplementationBulkAssign'
import { InternalCategory, InternalCategoryFeature } from '../types/InternalCategory'
import { InternalConcept } from '../types/InternalTag'
import { optionalJsonOrText } from './helpers'
import { categoryFeaturesTag, internalApiserverApi } from './internal-apiserver'
import { InternalGameScreenshot } from './screenshot-links'

// The writes' answers aren't used, and may be empty or plain text.
type BaseQuery = (arg: string | { url: string; method?: string; body?: unknown; responseHandler?: typeof optionalJsonOrText }) => Promise<{
  data?: unknown
  error?: FetchBaseQueryError
}>

type PickerCategory = InternalCategory & { type?: string | null; icon?: { url?: string | null } | null }

export type BulkAssignPickerFeaturesResult = { categories: PickerCategory[]; featuresByCategory: Record<string, InternalCategoryFeature[]> }

export type BulkAssignConceptsResult = { screenshots: InternalGameScreenshot[]; concepts: InternalConcept[]; readBack: ConceptsReadBack }

const listTag = 'internalBulkAssignScreenshots' as const

// Commas stay unencoded, as legacy sends them.
const idPath = (ids: (string | number)[]) => ids.map((id) => encodeURIComponent(String(id))).join(',')

// Both sides of the link: the screenshots' `conceptIds` and the concepts' `screenshotIds`.
const readConceptsBack = async (baseQuery: BaseQuery, groups: ConceptMatchGroup[], expectMatched: boolean) => {
  const screenshotIds = Array.from(new Set(groups.flatMap(({ screenshotsIds }) => screenshotsIds)))
  const screenshots = await baseQuery({ url: 'screenshots/list', method: 'POST', body: screenshotIds })
  if (screenshots.error) return { error: screenshots.error }
  const concepts = await baseQuery('concepts')
  if (concepts.error) return { error: concepts.error }
  const storedScreenshots = (screenshots.data ?? []) as InternalGameScreenshot[]
  const storedConcepts = (concepts.data ?? []) as InternalConcept[]
  const links = storedScreenshots.map(({ id, conceptIds }) => ({ screenshotId: id, conceptIds: conceptIds ?? [] }))
  return {
    data: {
      screenshots: storedScreenshots,
      concepts: storedConcepts,
      readBack: mergeConceptsReadBacks(getConceptsReadBack(groups, links, expectMatched), getConceptListReadBack(groups, storedConcepts, expectMatched)),
    },
  }
}

// Every list shows the screenshots' concepts, and Implementation Examples and the analyses read the same documents.
const writtenTags = (groups: ConceptMatchGroup[]) => [
  listTag,
  'internalConcepts' as const,
  'internalAnalysis' as const,
  ...Array.from(new Set(groups.flatMap(({ screenshotsIds }) => screenshotsIds))).map((id) => ({ type: 'internalImplementationScreenshots' as const, id })),
]

const implementationBulkAssignApi = internalApiserverApi
  .enhanceEndpoints({ addTagTypes: [listTag, 'internalConcepts', 'internalAnalysis', 'internalImplementationScreenshots'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // The US features of the user categories, as legacy's picker.
      getBulkAssignPickerFeatures: builder.query<BulkAssignPickerFeaturesResult, void>({
        async queryFn(_arg, _api, _extraOptions, baseQuery) {
          const map = await baseQuery(`i/categories/map/${bulkAssignMarketIso}`)
          if (map.error) return { error: map.error }
          const categories = Object.values((map.data ?? {}) as Record<string, PickerCategory>)
            .filter((category) => category.type === 'user')
            .sort((a, b) => a.ordr - b.ordr)
          const lists = await Promise.all(categories.map((category) => baseQuery(`i/categories/${category.id}/features/${bulkAssignMarketIso}`)))
          const failed = lists.find((list) => list.error)
          if (failed?.error) return { error: failed.error }
          return {
            data: {
              categories,
              featuresByCategory: Object.fromEntries(
                categories.map((category, index) => [category.id, (lists[index].data ?? []) as InternalCategoryFeature[]])
              ),
            },
          }
        },
        providesTags: (result) => ['internalCategories', 'internalFeatures', ...(result?.categories ?? []).map(({ id }) => categoryFeaturesTag(id))],
      }),
      getBulkAssignChoices: builder.query<BulkAssignStoredChoice[], void>({
        query: () => 'features/choices/screenshots',
        providesTags: ['internalFeatures'],
      }),
      // Every market's counts, as legacy.
      getBulkAssignCounts: builder.query<BulkAssignCounts, void>({
        async queryFn(_arg, _api, _extraOptions, baseQuery) {
          const answers = await Promise.all([
            baseQuery('screenshots/count'),
            ...choiceByChoiceFeatureLegacyIds.map((legacyId) => baseQuery(`screenshots/count?featureLegacyId=${legacyId}`)),
          ])
          const failed = answers.find((answer) => answer.error)
          if (failed?.error) return { error: failed.error }
          const [byFeature, ...byChoice] = answers.map((answer) => (answer.data ?? {}) as Record<string, number>)
          return { data: { byFeature, byChoice: Object.assign({}, ...byChoice) } }
        },
      }),
      // With choices, the screenshots having any of them (and any picked concept); without, those having any picked concept.
      getBulkAssignFeatureScreenshots: builder.query<InternalGameScreenshot[], BulkAssignSelection>({
        async queryFn({ choiceIds, conceptIds }, _api, _extraOptions, baseQuery) {
          if (!choiceIds.length && !conceptIds.length) return { data: [] }
          const answer = await baseQuery(choiceIds.length ? `screenshots/features?choices=${idPath(choiceIds)}` : `screenshots/concepts/${idPath(conceptIds)}`)
          if (answer.error) return { error: answer.error }
          const screenshots = (answer.data ?? []) as InternalGameScreenshot[]
          return { data: choiceIds.length ? filterByAnyConcept(screenshots, conceptIds) : screenshots }
        },
        providesTags: [listTag],
      }),
      getBulkAssignGameScreenshots: builder.query<InternalGameScreenshot[], string[]>({
        query: (gameIds) => `screenshots/list/${idPath(gameIds)}`,
        providesTags: [listTag],
      }),
      getBulkAssignGames: builder.query<Game[], string[]>({
        query: (gameIds) => ({ url: 'games/list', method: 'POST', body: { gameIds, include: bulkAssignGameFields.join(',') } }),
      }),
      // Changes customer-visible Implementation Examples in every market. Stops at the first failed call.
      matchBulkAssignConcepts: builder.mutation<BulkAssignConceptsResult, { groups: ConceptMatchGroup[] }>({
        async queryFn({ groups }, _api, _extraOptions, baseQuery) {
          for (const group of groups) {
            const matched = await baseQuery({ url: 'i/concepts/screenshots/match', method: 'POST', body: group, responseHandler: optionalJsonOrText })
            if (matched.error) return { error: matched.error }
          }
          return readConceptsBack(baseQuery as BaseQuery, groups, true)
        },
        invalidatesTags: (_result, _error, { groups }) => writtenTags(groups),
      }),
      unmatchBulkAssignConcepts: builder.mutation<BulkAssignConceptsResult, { group: ConceptMatchGroup }>({
        async queryFn({ group }, _api, _extraOptions, baseQuery) {
          const unmatched = await baseQuery({ url: 'i/concepts/screenshots/unmatch', method: 'POST', body: group, responseHandler: optionalJsonOrText })
          if (unmatched.error) return { error: unmatched.error }
          return readConceptsBack(baseQuery as BaseQuery, [group], false)
        },
        invalidatesTags: (_result, _error, { group }) => writtenTags([group]),
      }),
    }),
  })

export const {
  useGetBulkAssignPickerFeaturesQuery,
  useGetBulkAssignChoicesQuery,
  useGetBulkAssignCountsQuery,
  useGetBulkAssignFeatureScreenshotsQuery,
  useGetBulkAssignGameScreenshotsQuery,
  useGetBulkAssignGamesQuery,
  useMatchBulkAssignConceptsMutation,
  useUnmatchBulkAssignConceptsMutation,
} = implementationBulkAssignApi
